import { gql } from '@apollo/client';

import { mutate, query } from '../apollo/ApolloClient';
import { GraphQLError } from './graphQLError';

interface LoginResult {
  token: string;
}

interface LoginResponse {
  login: LoginResult;
}

interface LoginVars {
  email: string;
  password: string;
}

const LOGIN_QUERY = gql`
  query login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
    }
  }
`;

interface AuthenticationResult {
  err: boolean;
  context?: string;
}

export const loginUser = async (email: string, password: string): Promise<AuthenticationResult> => {
  try {
    const data = await query<LoginResponse, LoginVars>({
      query: LOGIN_QUERY,
      variables: {
        email,
        password,
      },
    });

    const { token } = data!.login;
    localStorage.setItem('token', token);
    return { err: false };
  } catch (error) {
    console.error(`[login] error: ${JSON.stringify(error)}`);
    return {
      err: true,
      context: (error as GraphQLError).message,
    };
  }
};

const VERIFY_QUERY = gql`
  query verify($dummy: Boolean) {
    verify(dummy: $dummy)
  }
`;

export const verifyToken = async (): Promise<boolean> => {
  try {
    await query<unknown>({
      query: VERIFY_QUERY,
    });

    return true;
  } catch (error) {
    console.error(`[verify] error: ${JSON.stringify(error)}`);
    return false;
  }
};

interface SignupVars {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  mobileNumber: string;
}

const SIGNUP_QUERY = gql`
  mutation signup(
    $email: String!
    $firstName: String!
    $lastName: String!
    $companyName: String!
    $mobileNumber: String!
  ) {
    signup(
      email: $email
      firstName: $firstName
      lastName: $lastName
      companyName: $companyName
      mobileNumber: $mobileNumber
    )
  }
`;

export const signUpNewUser = async (
  email: string,
  firstName: string,
  lastName: string,
  companyName: string,
  mobileNumber: string
): Promise<AuthenticationResult> => {
  try {
    await mutate<unknown, SignupVars>({
      mutation: SIGNUP_QUERY,
      variables: {
        email,
        firstName,
        lastName,
        companyName,
        mobileNumber,
      },
    });

    return { err: false };
  } catch (error) {
    console.error(`[signup] error: ${JSON.stringify(error)}`);
    return {
      err: true,
      context: (error as GraphQLError).message,
    };
  }
};

export interface User {
  firstName: string;
  lastName: string;
  email: string;
}

interface UserResponse {
  getUser: User;
}

const GET_USER_QUERY = gql`
  query getUser($dummy: Boolean) {
    getUser(dummy: $dummy) {
      firstName
      lastName
      email
    }
  }
`;

export const getUser = async (): Promise<User> => {
  try {
    const data = await query<UserResponse>({
      query: GET_USER_QUERY,
    });

    const { getUser: user } = data!;
    return user;
  } catch (error) {
    console.error(`[getUser] error: ${JSON.stringify(error)}`);
    throw error;
  }
};

export const logoutUser = (): void => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

// TODO
// export const changePassword = async (body, cancelToken) => {
//   const { data: resp } = await axios.post(`${APIORIGIN}/api-service/v1/users/self/changePassword`, body, {
//     cancelToken,
//   });
//   return resp;
// };
